/**
 * Operations with Integers generator (6.PAFR.3.5).
 *
 * Add, subtract, multiply, and divide integers, with correct sign handling.
 * The answer is chosen/derived first and the problem is assembled around it, so
 * the displayed operands and the graded answer can never disagree. Division
 * problems are built from a product so the quotient is always an exact integer.
 *
 * Families:
 *   F1: a + b   (integer addition)
 *   F2: a - b   (integer subtraction)
 *   F3: a * b   (integer multiplication)
 *   F4: a / b   (integer division, exact)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "integer-operations" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  /** Magnitude range for additive operands. */
  readonly addMax: number;
  /** Magnitude range for multiplicative factors. */
  readonly mulMax: number;
  /** Probability an operand is negative. */
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], addMax: 12, mulMax: 6, pNeg: 0.35 },
  standard: { families: ["F1", "F2", "F3", "F4"], addMax: 25, mulMax: 9, pNeg: 0.5 },
  hard: { families: ["F1", "F2", "F3", "F4"], addMax: 60, mulMax: 12, pNeg: 0.6 },
  challenge: { families: ["F2", "F3", "F4"], addMax: 120, mulMax: 15, pNeg: 0.65 },
};

/** A signed integer of the given magnitude range, excluding zero. */
function signedInt(rng: Rng, max: number, pNeg: number): Rational {
  const mag = rng.int(1, max);
  return rat(mag * rng.sign(pNeg));
}

/** Wrap a negative value in parentheses for readable LaTeX, e.g. "(-5)". */
function paren(v: Rational): string {
  return v.isNegative() ? `(${v.toLatex()})` : v.toLatex();
}
function parenText(v: Rational): string {
  return v.isNegative() ? `(${v.toString()})` : v.toString();
}

export class IntegerOperationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let a: Rational;
      let b: Rational;
      let answer: Rational;
      let opLatex: string;
      let opText: string;
      let steps: { description: string; latex: string }[];

      switch (family) {
        case "F1": {
          a = signedInt(rng, cfg.addMax, cfg.pNeg);
          b = signedInt(rng, cfg.addMax, cfg.pNeg);
          answer = a.add(b);
          opLatex = `${a.toLatex()} + ${paren(b)}`;
          opText = `${a.toString()} + ${parenText(b)}`;
          steps = [
            {
              description:
                "Same signs: add and keep the sign. Different signs: subtract and take the sign of the larger absolute value.",
              latex: `${a.toLatex()} + ${paren(b)} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
        case "F2": {
          a = signedInt(rng, cfg.addMax, cfg.pNeg);
          b = signedInt(rng, cfg.addMax, cfg.pNeg);
          answer = a.sub(b);
          opLatex = `${a.toLatex()} - ${paren(b)}`;
          opText = `${a.toString()} - ${parenText(b)}`;
          steps = [
            {
              description: "Subtracting is adding the opposite. Rewrite, then add.",
              latex: `${a.toLatex()} + ${paren(b.neg())} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
        case "F3": {
          a = signedInt(rng, cfg.mulMax, cfg.pNeg);
          b = signedInt(rng, cfg.mulMax, cfg.pNeg);
          answer = a.mul(b);
          opLatex = `${a.toLatex()} \\times ${paren(b)}`;
          opText = `${a.toString()} * ${parenText(b)}`;
          steps = [
            {
              description:
                "Multiply the absolute values. Same signs give a positive product; different signs give a negative product.",
              latex: `${a.toLatex()} \\times ${paren(b)} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
        case "F4": {
          // Build from a product so the quotient is an exact integer.
          const quotient = signedInt(rng, cfg.mulMax, cfg.pNeg);
          const divisor = signedInt(rng, cfg.mulMax, cfg.pNeg);
          const dividend = quotient.mul(divisor);
          a = dividend;
          b = divisor;
          answer = quotient;
          opLatex = `${a.toLatex()} \\div ${paren(b)}`;
          opText = `${a.toString()} / ${parenText(b)}`;
          steps = [
            {
              description:
                "Divide the absolute values. Same signs give a positive quotient; different signs give a negative quotient.",
              latex: `${a.toLatex()} \\div ${paren(b)} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
      }

      // Quality gate: skip the most trivial zero answers so practice stays useful.
      if (answer.isZero() && ctx.difficulty !== "easy") continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Compute: ${opText}`,
          latex: `${opLatex} = \\;?`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "Handle the signs first, then the arithmetic." },
          {
            level: 2,
            text: "Subtracting is adding the opposite; a product/quotient is negative only when the signs differ.",
          },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const integerOperations = new IntegerOperationsGenerator();
